// LoginScreen - REQ-AUTH-001.
//
// 화면에 할 일이 하나뿐이라 여기만 밀도가 낮다(체감 3). 회사명과 시스템명
// 외에 어떤 정보도 노출하지 않는다 - 사용자 수·개발품 수는 인증 전에
// 알려줄 이유가 없다.
//
// 인증 실패는 **폼 하단 인라인 에러**다. 토스트로 띄우지 않는다 - 로그인
// 실패는 폼의 상태이고 지나가는 사건이 아니다(디자인 가이드 §1).
// 계정 존재 여부를 구분해 알려주지 않는다.
import { Button, Card, Field, Icon, TextInput } from "@/components/ui";
import { useAuth } from "@/contexts/AuthContext";
import { useState } from "react";

export const LoginScreen = () => {
  const { signInWithEmail, signInWithGoogle } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});
  const [formError, setFormError] = useState("");
  const [busy, setBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);

  const google = async () => {
    setFieldErrors({});
    setFormError("");
    setGoogleBusy(true);
    try {
      await signInWithGoogle();
    } catch (err) {
      // 사용자가 팝업을 직접 닫은 것은 실패가 아니다 - 문구를 띄우면
      // "취소했는데 왜 오류지" 가 된다. 조용히 원상태로 돌아간다.
      const raw = err instanceof Error ? err.message : "";
      if (/popup-closed-by-user|cancelled-popup-request|user-cancelled/i.test(raw)) return;
      setFormError(
        /popup-blocked/i.test(raw)
          ? "팝업이 차단되었어요. 브라우저에서 팝업을 허용한 뒤 다시 시도해 주세요."
          : /network|fetch|offline/i.test(raw)
            ? "서버에 연결할 수 없습니다. 잠시 후 다시 시도하세요"
            : "구글 로그인에 실패했어요. 잠시 후 다시 시도해 주세요."
      );
    } finally {
      setGoogleBusy(false);
    }
  };

  const submit = async () => {
    const fe: { email?: string; password?: string } = {};
    if (!email.trim()) fe.email = "이메일을 입력하세요";
    if (!password) fe.password = "비밀번호를 입력하세요";
    setFieldErrors(fe);
    setFormError("");
    if (Object.keys(fe).length > 0) return;

    setBusy(true);
    try {
      await signInWithEmail(email.trim(), password);
    } catch (err) {
      // 계정 존재 여부를 구분해 알려주지 않는다. 네트워크 오류만 따로 문구를 준다.
      const raw = err instanceof Error ? err.message : "";
      setFormError(
        /network|fetch|offline/i.test(raw)
          ? "서버에 연결할 수 없습니다. 잠시 후 다시 시도하세요"
          : "아이디 또는 비밀번호가 올바르지 않아요. 다시 확인해 주세요."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <main
      className="flex min-h-[100dvh] flex-col items-center justify-center px-s5 py-s10"
      style={{ background: "var(--surface-sunken)" }}
    >
      <div className="flex w-full max-w-[22rem] flex-col gap-s6">
        <header className="flex flex-col items-center gap-s2 text-center">
          <img
            src="/logo.png"
            alt=""
            width={40}
            height={40}
            style={{ borderRadius: "var(--radius-control)", display: "block" }}
          />
          <h1 className="text-display">신규 개발품 통합관리</h1>
          <p className="text-label" style={{ color: "var(--text-tertiary)" }}>
            한부금속
          </p>
        </header>

        <Card>
          {/* Enter 키가 [로그인] 과 같이 동작하도록 진짜 form 을 쓴다. */}
          <form
            className="flex flex-col gap-s5"
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <Field label="이메일" error={fieldErrors.email}>
              {(p) => (
                <TextInput
                  {...p}
                  type="email"
                  value={email}
                  onChange={setEmail}
                  disabled={busy}
                  placeholder="hong@hanbu.co.kr"
                />
              )}
            </Field>
            <Field label="비밀번호" error={fieldErrors.password}>
              {(p) => (
                <TextInput
                  {...p}
                  type="password"
                  value={password}
                  onChange={setPassword}
                  disabled={busy}
                />
              )}
            </Field>

            {formError && (
              <p
                className="flex items-start gap-s2 text-label"
                style={{ color: "var(--state-late)" }}
              >
                <Icon name="alert" size={14} />
                {formError}
              </p>
            )}

            <Button
              variant="primary"
              size="lg"
              type="submit"
              fullWidth
              loading={busy}
              disabled={googleBusy}
            >
              로그인
            </Button>
          </form>

          {/* 구분선. 두 방식은 대안 관계이므로 위아래로 쌓지 않고 사이를 끊는다. */}
          <div className="flex items-center gap-s3 py-s4" aria-hidden="true">
            <span className="h-px flex-1" style={{ background: "var(--border-subtle)" }} />
            <span className="text-caption" style={{ color: "var(--text-tertiary)" }}>
              또는
            </span>
            <span className="h-px flex-1" style={{ background: "var(--border-subtle)" }} />
          </div>

          <Button
            variant="secondary"
            size="lg"
            fullWidth
            onClick={() => void google()}
            loading={googleBusy}
            disabled={busy}
            icon={
              /* 구글 브랜드 마크는 지정 4색을 바꿀 수 없다. src/** 는 색 리터럴을
                 금지하므로(ds-gate) 토큰화 대상이 아닌 브랜드 자산으로 분리한다. */
              <img src="/brand/google-g.svg" alt="" width={18} height={18} aria-hidden="true" />
            }
          >
            구글 계정으로 로그인
          </Button>
        </Card>
      </div>
    </main>
  );
};
